/**
 * Main App Component - V3 (Supabase Auth)
 *
 * Uses AuthProvider context for all auth state.
 */

import React, { useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// V3 Auth
import { useAuth } from "./context/AuthContext";

// API
import { getJobResults } from "./utils/api";

// V3 Pages (new)
import LoginPageV3 from "./pages/LoginPageV3";
import SignupPageV3 from "./pages/SignupPageV3";
import AuthCallbackPage from "./pages/AuthCallbackPage";

// V2 Components (kept)
import Navbar from "./components/Navbar";
import DashboardPage from "./components/DashboardPage";
import HistoryPage from "./components/HistoryPage";
import PricingPage from "./components/PricingPage";
import ChatLayout from "./components/ChatLayout";
import LoadingScreen from "./components/LoadingScreen";
import ResultsPage from "./components/ResultsPage";
import AdminPage from "./components/AdminPage";
import Footer from "./components/Footer";

// Public Pages
import LandingPage from "./pages/LandingPageV2";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import NotFoundPage from "./pages/NotFoundPage";
import FreelancerOnboarding from "./pages/FreelancerOnboarding";

// ===============================================
// MAIN APP
// ===============================================

function App() {
  const { isAuthenticated, loading, logout, profile, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Search State
  const [currentView, setCurrentView] = useState("home");
  const [jobId, setJobId] = useState(null);
  const [searchParams, setSearchParams] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading LeadGen AI...</p>
        </div>
      </div>
    );
  }

  // Layout Control
  const hideHeaderFooter = ["/", "/login", "/signup", "/freelancer-setup"].includes(location.pathname);
  const showNavbar = !hideHeaderFooter;
  const showFooter = !hideHeaderFooter && location.pathname !== "/search";

  // Auth Handlers
  const handleLogout = async () => {
    try {
      await logout();
      setCurrentView("home");
      setJobId(null);
      setResults(null);
      setError(null);
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // Search Handlers
  const handleSearchStart = (params, id) => {
    setSearchParams(params);
    setJobId(id);
    setCurrentView("loading");
    setError(null);
  };

  const handleSearchComplete = (data) => {
    setResults(data);
    setCurrentView("results");
  };

  const handleSearchError = (errorMessage) => {
    setError(errorMessage);
    setCurrentView("home");
  };

  const handleBackToHome = () => {
    setCurrentView("home");
    setJobId(null);
    setResults(null);
    setError(null);
  };

  const handleViewResults = async (viewJobId, params) => {
    setJobId(viewJobId);
    setSearchParams(params);
    setCurrentView("loading");
    setError(null);
    
    try {
      const resultData = await getJobResults(viewJobId);
      setResults(resultData);
      setCurrentView("results");
      navigate("/search");
    } catch (err) {
      console.error("Failed to load results:", err);
      setError("Failed to load results. Please try again.");
      setCurrentView("home");
      navigate("/search");
    }
  };

  // Onboarding Guard — redirects unonboarded users to /freelancer-setup
  const RequireOnboarding = ({ children }) => {
    if (profile && !profile.is_onboarded) {
      return <Navigate to="/freelancer-setup" replace />;
    }
    return children;
  };

  // Search Page Content
  const SearchPageContent = () => (
    <>
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-900/20 border border-red-500/30 text-red-400">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}
      {currentView === "home" && <ChatLayout onSearchStart={handleSearchStart} onSearchComplete={handleSearchComplete} onSearchError={handleSearchError} />}
      {currentView === "loading" && (
        <LoadingScreen jobId={jobId} searchParams={searchParams} onComplete={handleSearchComplete} onError={handleSearchError} />
      )}
      {currentView === "results" && results && (
        <ResultsPage jobId={jobId} results={results} searchParams={searchParams} onBack={handleBackToHome} />
      )}
    </>
  );

  // Render
  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col">
      {showNavbar && (
        <Navbar 
          isAuthenticated={isAuthenticated} 
          user={profile ? { name: profile.name || profile.email, email: profile.email, avatar_url: profile.avatar_url, plan: profile.plan, is_admin: profile.is_admin } : null}
          onLogout={logout}
        />
      )}

      <main className={isAuthenticated && location.pathname === "/search" ? "flex-1 w-full overflow-hidden" : "flex-1 w-full"}>
        <Routes>
          <Route path="/" element={isAuthenticated ? <Navigate to="/search" replace /> : <LandingPage />} />
          <Route path="/login" element={!isAuthenticated ? <LoginPageV3 /> : <Navigate to="/search" replace />} />
          <Route path="/signup" element={!isAuthenticated ? <SignupPageV3 /> : <Navigate to="/search" replace />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />

          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pricing" element={<PricingPage />} />

          <Route path="/search" element={isAuthenticated ? <RequireOnboarding><SearchPageContent /></RequireOnboarding> : <Navigate to="/login" replace />} />
          <Route path="/dashboard" element={isAuthenticated ? <RequireOnboarding><DashboardPage /></RequireOnboarding> : <Navigate to="/login" replace />} />
          <Route path="/history" element={isAuthenticated ? <RequireOnboarding><HistoryPage onViewResults={handleViewResults} /></RequireOnboarding> : <Navigate to="/login" replace />} />

          <Route path="/freelancer-setup" element={isAuthenticated ? <FreelancerOnboarding /> : <Navigate to="/login" replace />} />
          <Route path="/admin" element={isAdmin ? <RequireOnboarding><AdminPage /></RequireOnboarding> : <Navigate to="/dashboard" replace />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      {showFooter && <Footer />}
    </div>
  );
}

export default App;
